<script lang="ts">
	import classNames from 'classnames';
	import { onMount } from 'svelte';
	import { PrimaryRoutes } from '../../constants/routes';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { DropType } from '#lib/features/checkout/constants/drop-types.js';
	import {
		ALL,
		getCartIconContext,
	} from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { getLiveSlug } from '#lib/features/live-drop-info/utils/getLiveSlug/getLiveSlug.js';
	import { CartNavBar } from '#lib/features/navigation/components/CartNavBar/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { GlobalSearch } from '#lib/features/search/global/components/GlobalSearch/index.js';
	import { getTimeContext } from '#lib/features/time/utils/getTimeContext/getTimeContext.js';
	import { WishlistDrawer } from '#lib/features/wishlist/components/WishlistDrawer/index.js';
	import { WishlistNavIcon } from '#lib/features/wishlist/components/WishlistNavIcon/index.js';
	import { getWishlistContext } from '#lib/features/wishlist/utils/getWishlistContext/index.js';
	import type { GetPrimaryCollectionsWithDropConfigHeaderQuery } from '#lib/websites/primary/queries/collections/__generated__/getPrimaryCollections.generated.js';
	import { page } from '$app/state';
	import { HeniPrimaryLogo } from '$global/assets/logos/HeniPrimaryLogo';
	import { IconLink } from '$global/components/IconLink';
	import { SearchPageContainer } from '$global/features/search/components/SearchPageContainer';
	import { createSearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { getDirectusDateTimestamp } from '$global/utils/getDirectusDateTimestamp';
	import { getImageUrl } from '$global/utils/getImageUrl';
	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	const combobox = createSearchCombobox();

	const { setDropTypes, enableCartIcon, hideCartIcon } = getCartIconContext();
	const { fetchTime, interval } = getTimeContext();
	const {
		cartItems: { clearCart },
		checkoutForm: { clearForm },
		orderConfirmation: { clearOrderConfirmation },
	} = getCheckoutContext();
	const { liveDropInfoCtx } = getLiveDropInfoContext();
	const wishlistCtx = getWishlistContext();

	const primaryCollections = page.data
		.primaryCollections as GetPrimaryCollectionsWithDropConfigHeaderQuery['Primary_Collection'];

	const checkIfLiveRelease = (slug: string) => {
		return liveDropInfoCtx.liveDropInfo?.liveSlugs.includes(
			getLiveSlug(slug, 'primary')
		);
	};

	let liveReleases = $derived(
		primaryCollections.filter((primaryCollection) => {
			return checkIfLiveRelease(primaryCollection?.drop_page?.slug as string);
		})
	);

	let releasesSiteLinks = $derived([
		{
			label: 'Releases',
			dataCy: 'releases',
			items: [
				...liveReleases.map((primaryCollection) => ({
					title: `${primaryCollection.name}`,
					subtitle: `${primaryCollection.artists?.map((artist) => artist?.Artists_id?.name)?.join(', ')}`,
					src: `${getImageUrl(
						primaryCollection.icon_image?.filename_disk,
						'512px'
					)}`,
					href: `${PrimaryRoutes.Release}/${primaryCollection.drop_page?.slug || primaryCollection?.slug}`,
				})),
				{
					title: 'All releases',
					dataCy: 'all-releases',
					href: PrimaryRoutes.Releases,
					classes: {
						title: 'pl-[36px]',
					},
				},
			],
		},
	]);

	let siteLinks = $derived(
		liveReleases.length
			? releasesSiteLinks
			: [
					{
						label: 'Releases',
						dataCy: 'releases',
						href: PrimaryRoutes.Releases,
					},
				]
	);

	onMount(() => {
		return () => {
			if ($interval) {
				clearInterval($interval);
			}

			interval.set(null);
		};
	});

	let disableCart = $derived(
		page.url.pathname.startsWith(`${PrimaryRoutes.Apply}/`) ||
			page.url.pathname.startsWith(
				`${PrimaryRoutes.ApplicationPreferences}/`
			) ||
			page.url.pathname.startsWith(`${PrimaryRoutes.OrderConfirmed}/`) ||
			(page.url.pathname.startsWith(`${PrimaryRoutes.Checkout}/`) &&
				!!page.data.isGeneric)
	);

	$effect(() => {
		if (page.url.pathname.startsWith(PrimaryRoutes.OrderConfirmed)) {
			if (!page.data.isGeneric && !page.data.isProcessedOffline) {
				try {
					clearCart();
				} catch {
					// localStorage may not be available
				}
			}
		}
	});

	$effect(() => {
		if (liveDropInfoCtx.liveDropInfo) {
			// Fetches current UTC time, stores it globally time and set an interval that will update it every second
			fetchTime().then((currentTime) => {
				const dropTypes: Record<
					string,
					{ type: DropType; dropPageSlug?: string }
				> = { [ALL]: { type: DropType.Application } };

				// If the current time has not been returned
				// We don't enable the application process and clear the checkout form
				// Collection codes of active application drops — the wishlist is
				// application-only and cross-drop, keyed by collection code (CartItem.groupId).
				const activeWishlistGroupIds = new Set<string>();

				if (!currentTime) {
					hideCartIcon();
					clearForm();
					clearOrderConfirmation(ALL);
				} else {
					// we don't show the apply menu in the header
					// And we clear the checkout form states

					const activeDrops = Object.values(
						liveDropInfoCtx.liveDropInfo?.primary || []
					)?.reduce((accumulator: string[], drop) => {
						if (
							drop?.slug &&
							drop?.checkoutEndDate &&
							currentTime < getDirectusDateTimestamp(drop?.checkoutEndDate)
						) {
							const dropType = drop?.isProcessedOffline
								? DropType.Application
								: DropType.Purchase;

							if (drop?.slug) {
								dropTypes[drop.slug] = {
									type: dropType,
									dropPageSlug: drop?.slug,
								};
							}

							if (dropType === DropType.Application && drop?.code) {
								activeWishlistGroupIds.add(drop.code);
							}

							return [...accumulator, drop?.slug];
						}

						return accumulator;
					}, []);

					if (!activeDrops?.length) {
						hideCartIcon();
						clearForm();
						clearOrderConfirmation(ALL);
					} else if (
						Object.values(dropTypes).find(
							({ type }) => type === DropType.Purchase
						)
					) {
						enableCartIcon();
					}
				}

				setDropTypes(dropTypes);

				// Prune wishlist items whose drop has closed and toggle the heart icon.
				if (FeatureFlags.Wishlist) {
					wishlistCtx?.syncActiveDrops(
						activeWishlistGroupIds,
						activeWishlistGroupIds.size > 0
					);
				}
			});
		}
	});
</script>

<CartNavBar
	intermittentCart
	{disableCart}
	{combobox}
	SearchComponent={GlobalSearch}
	{siteLinks}
	isSearchEnabled
>
	{#snippet homeIcon()}
		<IconLink
			dataCy="header"
			href={GlobalRoutes.PrimaryHome}
			label="See HENI Primary home page"
			class="outline-none"
		>
			<HeniPrimaryLogo class="xs:scale-100 origin-left scale-75" />
		</IconLink>
	{/snippet}
	{#snippet wishlistIcon(showCartIcon: boolean)}
		{#if FeatureFlags.Wishlist}
			<WishlistNavIcon
				dataCy="wishlist-icon"
				class={classNames('mt-[6px] lg:mr-0 lg:ml-[0.875rem]', {
					'mr-[0.875rem]': showCartIcon,
				})}
			/>
		{/if}
	{/snippet}
</CartNavBar>

{#if FeatureFlags.Wishlist}
	<WishlistDrawer />
{/if}

<SearchPageContainer {combobox}>
	{@render children?.()}
</SearchPageContainer>
